import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';
import { copyNodeAsPng } from '../utils/copyNodeAsPng';
import './DealSummaryModal.css';

const DASH = '—';

function formatCardDate(date) {
  if (!date) return DASH;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return DASH;
  return parsed.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCardPercent(num, digits) {
  if (num === null || num === undefined || Number.isNaN(Number(num))) return DASH;
  return `${Number(num).toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

function getCloseDate(deal) {
  if (deal?.day?.close) return deal.day.close;
  return new Date();
}

function DealSummaryField({ label, value }) {
  return (
    <div className="deal-summary-card__field">
      <div className="deal-summary-card__label">{label}</div>
      <div className="deal-summary-card__value">{value}</div>
    </div>
  );
}

function shareButtonLabel(kind, shareState) {
  if (shareState === `${kind}-pending`) return 'Копирую…';
  if (shareState === kind) return 'Скопировано';
  if (shareState === `${kind}-error`) return 'Не удалось';
  return kind === 'link' ? 'Поделиться (ссылка)' : 'Поделиться (карточка)';
}

function DealSummaryModal({ isOpen, onClose, deal, userId, ignoreOpeningGesture = false }) {
  useModalSelectionGuard(isOpen);
  const [logoFailed, setLogoFailed] = useState(false);
  const [shareState, setShareState] = useState(null);
  const cardRef = useRef(null);
  const suppressOverlayCloseRef = useRef(false);

  const joinUrl = useMemo(() => {
    const url = new URL('https://exponenta.online/');
    if (userId) {
      url.searchParams.set('ref', String(userId));
    }
    return url;
  }, [userId]);

  useEffect(() => {
    setLogoFailed(false);
    setShareState(null);
  }, [deal?.stockLogoLink, deal?.id, isOpen]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (suppressOverlayCloseRef.current) return;
    if (e.target !== e.currentTarget) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      suppressOverlayCloseRef.current = false;
      return undefined;
    }

    if (!ignoreOpeningGesture) {
      suppressOverlayCloseRef.current = false;
      return undefined;
    }

    suppressOverlayCloseRef.current = true;
    let released = false;

    const releaseOpeningGesture = () => {
      if (released) return;
      released = true;
      window.setTimeout(() => {
        suppressOverlayCloseRef.current = false;
      }, 0);
    };

    window.addEventListener('pointerup', releaseOpeningGesture, true);
    window.addEventListener('pointercancel', releaseOpeningGesture, true);
    return () => {
      window.removeEventListener('pointerup', releaseOpeningGesture, true);
      window.removeEventListener('pointercancel', releaseOpeningGesture, true);
    };
  }, [isOpen, ignoreOpeningGesture]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel]);

  useEffect(() => {
    if (!shareState || String(shareState).endsWith('-pending')) return undefined;
    const timeoutId = window.setTimeout(() => setShareState(null), 2000);
    return () => window.clearTimeout(timeoutId);
  }, [shareState]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl.href);
      setShareState('link');
    } catch {
      setShareState('link-error');
    }
  }, [joinUrl.href]);

  const handleCopyCard = useCallback(async () => {
    try {
      setShareState('card-pending');
      await copyNodeAsPng(cardRef.current);
      setShareState('card');
    } catch {
      setShareState('card-error');
    }
  }, []);

  if (!isOpen || !deal) return null;

  const calc = deal.calculatedFields || {};
  const spreadOpen = calc.spread?.open;
  const spreadClose = calc.spread?.close;
  const spreadRes = calc.spreadRes;
  const daysLong = calc.daysLong;
  const annualized = calc.nTotalAllYearPercent;
  const logoSrc = deal.stockLogoLink;
  const showLogo = Boolean(logoSrc) && !logoFailed;

  return createPortal(
    <div
      className="deal-summary-modal-overlay"
      onClick={handleOverlayClick}
      onSelectStart={preventModalOverlaySelection}
    >
      <div
        className="deal-summary-modal"
        role="dialog"
        aria-label="Карточка сделки"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="deal-summary-modal__chrome deal-summary-modal__close"
          onClick={handleCancel}
          aria-label="Закрыть"
        >
          ×
        </button>
        <div className="deal-summary-card" ref={cardRef}>
          <a
            className="deal-summary-card__brand"
            href={joinUrl.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/logo192.png"
              alt=""
              className="deal-summary-card__brand-logo"
            />
            <span className="deal-summary-card__brand-name">exponenta.online</span>
          </a>
          <div className="deal-summary-card__top">
            <div className="deal-summary-card__ticker-block">
              <div className="deal-summary-card__annual">
                <span className="deal-summary-card__annual-value">
                  {formatCardPercent(annualized, 1)}
                </span>
                {' '}
                <span className="deal-summary-card__annual-label">ГОДОВЫХ</span>
              </div>
              <div className="deal-summary-card__label">Тикер</div>
              <div className="deal-summary-card__value deal-summary-card__value--ticker">
                {deal.futures || DASH}
              </div>
            </div>
            <div className="deal-summary-card__logo" aria-hidden={!showLogo}>
              {showLogo ? (
                <img
                  src={logoSrc}
                  alt=""
                  className="deal-summary-card__logo-img"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span className="deal-summary-card__logo-fallback">{deal.stock || ''}</span>
              )}
            </div>
          </div>
          <div className="deal-summary-card__row">
            <DealSummaryField label="Дата Открытия" value={formatCardDate(deal.day?.open)} />
            <DealSummaryField label="Дата закрытия" value={formatCardDate(getCloseDate(deal))} />
          </div>
          <div className="deal-summary-card__row">
            <DealSummaryField label="Спред входа" value={formatCardPercent(spreadOpen, 2)} />
            <DealSummaryField label="Спред выхода" value={formatCardPercent(spreadClose, 2)} />
          </div>
          <div className="deal-summary-card__row">
            <DealSummaryField label="Результат по сделке" value={formatCardPercent(spreadRes, 2)} />
            <DealSummaryField
              label="Кол-во дней в сделке"
              value={daysLong === null || daysLong === undefined ? DASH : String(daysLong)}
            />
          </div>
          <div className="deal-summary-card__footer">
            <p className="deal-summary-card__cta">
              Хочешь так же?{' '}
              <a
                href={joinUrl.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                Присоединяйся
              </a>
              .
            </p>
            <a
              className="deal-summary-card__qr"
              href={joinUrl.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Реферальная ссылка"
            >
              <QRCodeSVG
                value={joinUrl.href}
                size={76}
                bgColor="#ffffff"
                fgColor="#18181b"
                marginSize={1}
                title="Реферальная ссылка"
              />
            </a>
          </div>
        </div>
        <div className="deal-summary-modal__actions">
          <button
            type="button"
            className="deal-summary-modal__chrome deal-summary-modal__share"
            onClick={handleCopyLink}
          >
            {shareButtonLabel('link', shareState)}
          </button>
          <button
            type="button"
            className="deal-summary-modal__chrome deal-summary-modal__share"
            onClick={handleCopyCard}
            disabled={shareState === 'card-pending'}
          >
            {shareButtonLabel('card', shareState)}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DealSummaryModal;
